/**
 * Icon Resolver
 *
 * Resolves icon references to React components.
 *
 * Supports:
 * - IconConfig objects: { name: 'Archive', size: 20 }
 * - React elements: <CustomIcon />
 */
import React from 'react';
import { Icon, type IconConfig } from '@lognai/synapse-design-system-v1';

/**
 * Check if value is an IconConfig object
 */
function isIconConfig(icon: any): icon is IconConfig {
  return (
    typeof icon === 'object' &&
    icon !== null &&
    'name' in icon &&
    typeof icon.name === 'string'
  );
}

/**
 * Resolve an icon to a React element
 *
 * Accepts:
 * - IconConfig object: { name: 'Archive', size: 20 }
 * - React element: <CustomIcon />
 * - undefined: returns default Circle icon
 *
 * @param icon - Icon config object or React element
 * @param defaultSize - Default icon size (default: 20)
 * @returns React element for the icon
 */
export function resolveIconComponent(
  icon?: IconConfig | React.ReactElement,
  defaultSize: number = 20
): React.ReactElement {
  // No icon provided, use default
  if (!icon) {
    return React.createElement(Icon, { name: 'Circle', size: defaultSize });
  }

  // Icon config object
  if (isIconConfig(icon)) {
    return React.createElement(Icon, {
      name: icon.name,
      size: icon.size ?? defaultSize,
    });
  }

  // Already a React element
  if (React.isValidElement(icon)) {
    return icon;
  }

  // Invalid type
  throw new Error(
    `Icon must be an IconConfig object or React element. ` +
      `Received: ${typeof icon}. ` +
      `Example: icon: { name: 'Archive', size: 20 }`
  );
}

